import { Component, Inject, OnInit } from "@angular/core";
import {
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
} from "@angular/forms";
import { AccessFlowRuleset } from "@aptsi-types";
import { ActivatedRoute, Router } from "@angular/router";
import { AccessFlowRulesetService } from "./access-flow-ruleset.service";
import { RulesetService } from "../../ruleset/ruleset.service";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { ErrorDialogComponent } from "../../shared/dialogs/error-dialog.component";
import { SuccessDialogComponent } from "../../shared/dialogs/success-dialog/success-dialog.component";
import { EnvironmentService } from "src/app/core/service/environment.service";
@Component({
  selector: "app-access-flow-ruleset-create-or-update",
  templateUrl: "./access-flow-ruleset-create-or-update.component.html",
  styleUrls: ["./access-flow-ruleset-create-or-update.component.scss"],
})
export class AccessFlowRulesetCreateOrUpdateComponent implements OnInit {
  // Access Flow Context
  isCreate = false;
  existingAccessFlowId: number = null;
  accessFlowRuleset: AccessFlowRuleset = null;
  showModal: boolean = false;

  // Form
  form: FormGroup;
  readonly typeOptions = [
    "risk_score",
    "asset_access_threshold",
    "asset_access_determination",
    "None",
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private accessFlowRulesetService: AccessFlowRulesetService,
    private dialog: MatDialog,
    private ruleSetService: RulesetService,
    public environmentService: EnvironmentService 
    
  ) {}

  // Lifecycle
  async ngOnInit(): Promise<void> {
    this.isCreate = Boolean(this.route.snapshot.data.create);
    this.existingAccessFlowId = +this.route.snapshot.params.accessFlowId;

    if (!this.isCreate) {
      this.fetchAccessFlowRulesets();
    }

    this.form = this.isCreate
      ? new FormGroup({
          rulesetId: new FormControl(null, Validators.required),
          accessFlowId: new FormControl(
            this.existingAccessFlowId,
            Validators.required
          ),
          rulesetType: new FormControl("None", [
            Validators.required,
            this.typeValidator,
          ]),
        })
      : new FormGroup({
          accessControlFlowRulesetId: new FormControl(
            this.accessFlowRuleset ? this.accessFlowRuleset.accessflowid : null,
            Validators.required
          ),
          rulesetId: new FormControl(
            this.accessFlowRuleset ? this.accessFlowRuleset.rulesetid : null,
            Validators.required
          ),
          accessFlowId: new FormControl(
            this.existingAccessFlowId,
            Validators.required
          ),
          rulesetType: new FormControl(
            this.accessFlowRuleset
              ? this.accessFlowRuleset.rulesetType
              : "None",
            [Validators.required, this.typeValidator]
          ),
        });
  }

  // Validators
  typeValidator(control: AbstractControl): { [key: string]: any } | null {
    const selectedType = control.value;
    return selectedType === "None" ? { noneSelected: true } : null;
  }

  // Fetching
  async fetchAccessFlowRulesets() {
    try {
      const response = await this.accessFlowRulesetService
        .getAccessFlowRulesetByaccessFlowId(this.existingAccessFlowId)
        .toPromise();

      this.accessFlowRuleset = response["Resource retrieved successfully."]
        ? response["Resource retrieved successfully."][0]
        : null;

      if (this.accessFlowRuleset) {
        this.form.patchValue({
          accessControlFlowRulesetId:
            this.accessFlowRuleset.accessflowrulesetid,
          rulesetId: this.accessFlowRuleset.rulesetid,
          rulesetType: this.accessFlowRuleset.rulesetType,
        });
      }
    } catch (error) {
      console.error("Error fetching accessflows:", error);
    }
  }

  // Navigation
  cancel() {
    this.router.navigate(["ada-access/auth-tools/access-flow/search"]);
  }

  gotToRuleset() {
    this.router.navigate(["ada-access/auth-tools/ruleset/create"]);
  }

  // Submit Logic
  async submit() {
    try {
      this.form.get("accessControlFlowRulesetId").setErrors(null);
      this.form.get("rulesetId").setErrors(null);

      if (this.isCreate) {
        const rulesetIdExists = await this.checkRulesetIdExists(
          this.form.value.rulesetId
        );

        if (!rulesetIdExists) {
          this.form.get("rulesetId").setErrors({ rulesetIdNotExists: true });
          return;
        }

        await this.accessFlowRulesetService
          .createAccessFlowRuleset(
            this.form.value.accessControlFlowRulesetId,
            this.form.value.rulesetType,
            this.form.value.rulesetId
          )
          .toPromise();
      } else {
        await this.accessFlowRulesetService
          .updateAccessFlowRuleset(
            this.existingAccessFlowId,
            this.form.value.accessControlFlowRulesetId,
            this.form.value.rulesetType,
            this.form.value.rulesetId
          )
          .toPromise();
      }

      this.openSuccessDialog(this.isCreate);
    } catch (error) {
      this.openErrorDialog(error.message || "An unexpected error occurred.");
    }
  }

  // ID Check Logic
  async checkAccessFlowRulesetIdExists(
    accessControlFlowRulesetId: number
  ): Promise<boolean> {
    try {
      const response = await this.accessFlowRulesetService
        .getAccessFlowRuleset(accessControlFlowRulesetId)
        .toPromise();

      const exists = response["Resource retrieved successfully."] as any;
      return exists && exists.length > 0;
    } catch (error) {
      console.error("Error checking accessFlowRulesetId existence:", error);
      return false;
    }
  }

  async checkRulesetIdExists(rulesetId: string): Promise<boolean> {
    try {
      const response = await this.ruleSetService
        .getRuleset(rulesetId)
        .toPromise();

      const exists = response["Resource retrieved successfully."] as any;
      return exists && exists.length > 0;
    } catch (error) {
      console.error("Error checking rulesetId existence:", error);
      return false;
    }
  }

  // Dialogs
  openSuccessDialog(isCreate: boolean): void {
    const dialogRef = this.dialog.open(SuccessDialogComponent, {
      width: "250px",
      data: { isCreate: isCreate, title: "Access Flow Ruleset" },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.router.navigate(["ada-access/auth-tools/access-flow/search"]);
    });
  }

  openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      width: "250px",
      data: { errorMessage: errorMessage },
    });
  }
}
